import { Col } from '@rematter/ui-web';

import type { CycleCountTaskDetailRecord } from '../../_graphql/queryCycleCountTask';
import { useTranslations } from '../../i18n/useTranslations';

import { CountLineRow } from './CountLineRow';

type CycleCountTaskLinesContentProps = {
  task: CycleCountTaskDetailRecord;
  isEditable: boolean;
  showLineActions: boolean;
  localCounts: Record<string, number | null>;
  onCountedQuantityChange: (lineId: string, value: number | null) => void;
  onApproveAdjustment: (lineId: string, unitCostOverride?: number) => void;
  onRejectAdjustment: (lineId: string) => void;
  onRequestRecount: (lineId: string) => void;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const CycleCountTaskLinesContent = ({
  task,
  isEditable,
  showLineActions,
  localCounts,
  onCountedQuantityChange,
  onApproveAdjustment,
  onRejectAdjustment,
  onRequestRecount,
}: CycleCountTaskLinesContentProps) => {
  const t = useTranslations();

  return (
    <Col gap4 className="@CycleCountTaskLinesContent p-4">
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-neutral/10 p-4 sm:grid-cols-4">
        <div>
          <span className="text-xs text-neutral/50">
            {t.cycleCountTasksView.detail.info.countType}
          </span>
          <p className="text-sm font-medium">
            {t.cycleCountTasksView.countTypes[task.countType] ?? task.countType}
          </p>
        </div>
        <div>
          <span className="text-xs text-neutral/50">
            {t.cycleCountTasksView.detail.info.assignedTo}
          </span>
          <p className="text-sm font-medium">
            {task.assignedToUserName || '\u2014'}
          </p>
        </div>
        <div>
          <span className="text-xs text-neutral/50">
            {t.cycleCountTasksView.detail.info.dueDate}
          </span>
          <p className="text-sm font-medium">{formatDate(task.dueDate)}</p>
        </div>
        <div>
          <span className="text-xs text-neutral/50">
            {t.cycleCountTasksView.detail.info.blindCount}
          </span>
          <p className="text-sm font-medium">
            {task.isBlindCount
              ? t.cycleCountTasksView.detail.info.yes
              : t.cycleCountTasksView.detail.info.no}
          </p>
        </div>
        {task.startedAt && (
          <div>
            <span className="text-xs text-neutral/50">
              {t.cycleCountTasksView.detail.info.startedAt}
            </span>
            <p className="text-sm font-medium">{formatDate(task.startedAt)}</p>
          </div>
        )}
        {task.completedAt && (
          <div>
            <span className="text-xs text-neutral/50">
              {t.cycleCountTasksView.detail.info.completedAt}
            </span>
            <p className="text-sm font-medium">
              {formatDate(task.completedAt)}
            </p>
          </div>
        )}
        {task.approvedAt && (
          <div>
            <span className="text-xs text-neutral/50">
              {t.cycleCountTasksView.detail.info.approvedAt}
            </span>
            <p className="text-sm font-medium">{formatDate(task.approvedAt)}</p>
          </div>
        )}
        {task.approvedByUserName && (
          <div>
            <span className="text-xs text-neutral/50">
              {t.cycleCountTasksView.detail.info.approvedBy}
            </span>
            <p className="text-sm font-medium">{task.approvedByUserName}</p>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral/10">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-neutral/10 bg-neutral/3">
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.item}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.account}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.expected}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.counted}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.variance}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.tolerance}
              </th>
              <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                {t.cycleCountTasksView.detail.linesTable.adjustmentStatus}
              </th>
              {showLineActions && (
                <th className="px-3 py-2 text-xs font-medium text-neutral/60">
                  {t.cycleCountTasksView.detail.linesTable.actions}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {task.lines.map((line) => (
              <CountLineRow
                key={line.id}
                line={line}
                isBlindCount={task.isBlindCount && isEditable}
                isEditable={isEditable}
                showLineActions={showLineActions}
                onCountedQuantityChange={onCountedQuantityChange}
                onApproveAdjustment={onApproveAdjustment}
                onRejectAdjustment={onRejectAdjustment}
                onRequestRecount={onRequestRecount}
                localCountedQuantity={localCounts[line.id]}
              />
            ))}
            {task.lines.length === 0 && (
              <tr>
                <td
                  colSpan={showLineActions ? 8 : 7}
                  className="px-3 py-8 text-center text-sm text-neutral/40"
                >
                  {t.cycleCountTasksView.detail.noLines}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Col>
  );
};
